import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** Shared wizard field / section / placeholder labels for all case-request flows. */
export function useWizardFieldLabels() {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      sectionCaseCategorization: t('wizard.sections.caseCategorization'),
      sectionCaseIntakeDetails: t('wizard.sections.caseIntakeDetails'),
      sectionCaseDetails: t('wizard.sections.caseDetails'),
      sectionCategorization: t('wizard.sections.categorization'),
      sectionVictimDetails: t('wizard.sections.victimDetails'),
      sectionOffenceDetails: t('wizard.sections.offenceDetails'),
      sectionTimeline: t('wizard.sections.timeline'),
      sectionRequestSubmitterDetails: t('wizard.sections.requestSubmitterDetails'),
      sectionPhysicalFileDeliveryMethod: t('wizard.sections.physicalFileDeliveryMethod'),
      sectionPartyDetails: t('wizard.sections.partyDetails'),
      sectionIncidentMatterDetails: t('wizard.sections.incidentMatterDetails'),
      sectionRequesterDetails: t('wizard.sections.requesterDetails'),
      sectionInformationRequestDetails: t('wizard.sections.informationRequestDetails'),
      sectionAdditionalInformation: t('wizard.sections.additionalInformation'),
      sectionPetitionerDetails: t('wizard.sections.petitionerDetails'),
      sectionRespondentDetails: t('wizard.sections.respondentDetails'),
      sectionAccusedPersonDetails: t('wizard.sections.accusedPersonDetails'),
      sectionLegalExtraditionCaseDetails: t('wizard.sections.legalExtraditionCaseDetails'),
      sectionUploadDocuments: t('wizard.sections.uploadDocuments'),
      sectionPartiesInvolved: t('wizard.sections.partiesInvolved'),
      sectionSubmission: t('wizard.sections.submission'),
      fileCategory: t('wizard.fields.fileCategory'),
      subFileCategory: t('wizard.fields.subFileCategory'),
      applicableAreas: t('wizard.fields.applicableAreas'),
      description: t('wizard.fields.description'),
      caseSource: t('wizard.fields.caseSource'),
      province: t('wizard.fields.province'),
      district: t('wizard.fields.district'),
      instituteType: t('wizard.fields.instituteType'),
      corporateInstitute: t('wizard.fields.corporateInstitute'),
      otherInstituteName: t('wizard.fields.otherInstituteName'),
      governmentDepartment: t('wizard.fields.governmentDepartment'),
      corporation: t('wizard.fields.corporation'),
      yourReferenceNumber: t('wizard.fields.yourReferenceNumber'),
      otherExternalReferences: t('wizard.fields.otherExternalReferences'),
      caseNumber: t('wizard.fields.caseNumber'),
      connectedMatters: t('wizard.fields.connectedMatters'),
      connectedMattersShort: t('wizard.fields.connectedMattersShort'),
      connectedCaseNumber: t('wizard.fields.connectedCaseNumber'),
      connectedAgdFileRef: t('wizard.fields.connectedAgdFileRef'),
      hasPreviousAdviceOnMatter: t('wizard.fields.hasPreviousAdviceOnMatter'),
      hasPreviousAdvice: t('wizard.fields.hasPreviousAdvice'),
      previousAdviceAttachment: t('wizard.fields.previousAdviceAttachmentIfYes'),
      connectedCaseRelevant: t('wizard.fields.connectedCaseRelevant'),
      connectedCaseDetails: t('wizard.fields.connectedCaseDetails'),
      connectedAdviceCase: t('wizard.fields.connectedAdviceCase'),
      connectedAdviceCaseDetails: t('wizard.fields.connectedAdviceCaseDetails'),
      appealMatter: t('wizard.fields.appealMatter'),
      isAppealMatter: t('wizard.fields.isAppealMatter'),
      originalCourt: t('wizard.fields.originalCourt'),
      judgementOrder: t('wizard.fields.judgementOrder'),
      judgementOrderShort: t('wizard.fields.judgementOrderShort'),
      highCourtName: t('wizard.fields.highCourtName'),
      magistrateCourtName: t('wizard.fields.magistrateCourtName'),
      policeStation: t('wizard.fields.policeStation'),
      hasActionInstituted: t('wizard.fields.hasActionInstituted'),
      courtType: t('wizard.fields.courtType'),
      courtTypeOther: t('wizard.fields.courtTypeOther'),
      courtName: t('wizard.fields.courtName'),
      countryInvolved: t('wizard.fields.countryInvolved'),
      directionOfRequest: t('wizard.fields.directionOfRequest'),
      caseMatter: t('wizard.fields.caseMatter'),
      criminalMatter: t('wizard.fields.criminalMatter'),
      civilMatter: t('wizard.fields.civilMatter'),
      available: t('wizard.fields.available'),
      victimName: t('wizard.fields.victimName'),
      dateOfBirth: t('wizard.fields.dateOfBirth'),
      age: t('wizard.fields.age'),
      gender: t('wizard.fields.gender'),
      dateOfOffence: t('wizard.fields.dateOfOffence'),
      placeOfOffence: t('wizard.fields.placeOfOffence'),
      accusedDetailsAvailable: t('wizard.fields.accusedDetailsAvailable'),
      accusedSuspects: t('wizard.fields.accusedSuspects'),
      urgencyLevel: t('wizard.fields.urgencyLevel'),
      deadline: t('wizard.fields.deadline'),
      deadlineIfAny: t('wizard.fields.deadlineIfAny'),
      urgencyJustification: t('wizard.fields.urgencyJustification'),
      submittedDivisionUnit: t('wizard.fields.submittedDivisionUnit'),
      submittedDivisionUnitNoSpace: t('wizard.fields.submittedDivisionUnitNoSpace'),
      submittedAgencyName: t('wizard.fields.submittedAgencyName'),
      designation: t('wizard.fields.designation'),
      submittedBy: t('wizard.fields.submittedBy'),
      emailAddress: t('wizard.fields.emailAddress'),
      contactNumber: t('wizard.fields.contactNumber'),
      physicalFileDeliveryMethod: t('wizard.fields.physicalFileDeliveryMethod'),
      deliveryMethod: t('wizard.fields.deliveryMethod'),
      plaintiffApplicantName: t('wizard.fields.plaintiffApplicantName'),
      defendantRespondentName: t('wizard.fields.defendantRespondentName'),
      selectOneOfFollowing: t('wizard.fields.selectOneOfFollowing'),
      otherInvolvedInstitutions: t('wizard.fields.otherInvolvedInstitutions'),
      legalOfficerName: t('wizard.fields.legalOfficerName'),
      legalOfficerNameAlt: t('wizard.fields.legalOfficerNameAlt'),
      publicCorporationNames: t('wizard.fields.publicCorporationNames'),
      publicCorporationName: t('wizard.fields.publicCorporationName'),
      governmentMinistryDepartmentAlt: t('wizard.fields.governmentMinistryDepartmentAlt'),
      publicCorporationStatutoryAlt: t('wizard.fields.publicCorporationStatutoryAlt'),
      subjectOfDispute: t('wizard.fields.subjectOfDispute'),
      incidentOccurredDate: t('wizard.fields.incidentOccurredDate'),
      nextStepOfCase: t('wizard.fields.nextStepOfCase'),
      nextStepOfCaseIfApplicable: t('wizard.fields.nextStepOfCaseIfApplicable'),
      nextStepOther: t('wizard.fields.nextStepOther'),
      nextDateOfCase: t('wizard.fields.nextDateOfCase'),
      accusedName: t('wizard.fields.accusedName'),
      nationality: t('wizard.fields.nationality'),
      countryOfOrigin: t('wizard.fields.countryOfOrigin'),
      passportIdNumber: t('wizard.fields.passportIdNumber'),
      currentCustodyStatus: t('wizard.fields.currentCustodyStatus'),
      custodialAuthority: t('wizard.fields.custodialAuthority'),
      lastKnownLocation: t('wizard.fields.lastKnownLocation'),
      arrestWarrantAvailable: t('wizard.fields.arrestWarrantAvailable'),
      warrantReference: t('wizard.fields.warrantReference'),
      applicableTreatyArrangement: t('wizard.fields.applicableTreatyArrangement'),
      requestorName: t('wizard.fields.requestorName'),
      nicPassportNumber: t('wizard.fields.nicPassportNumber'),
      address: t('wizard.fields.address'),
      email: t('wizard.fields.email'),
      isSriLankanCitizen: t('wizard.fields.isSriLankanCitizen'),
      specificPublicAuthorityName: t('wizard.fields.specificPublicAuthorityName'),
      requestedInformationDetails: t('wizard.fields.requestedInformationDetails'),
      informationRequestedTimePeriod: t('wizard.fields.informationRequestedTimePeriod'),
      preferredMethodOfAccess: t('wizard.fields.preferredMethodOfAccess'),
      preferredLanguageForResponse: t('wizard.fields.preferredLanguageForResponse'),
      involvesLifeOrPersonalLiberty: t('wizard.fields.involvesLifeOrPersonalLiberty'),
      justification: t('wizard.fields.justification'),
      additionalNotes: t('wizard.fields.additionalNotes'),
      name: t('wizard.fields.name'),
      nicIdentificationNumber: t('wizard.fields.nicIdentificationNumber'),
      nic: t('wizard.fields.nic'),
      representedByAttorney: t('wizard.fields.representedByAttorney'),
      attorneyName: t('wizard.fields.attorneyName'),
      attorneyContactNumber: t('wizard.fields.attorneyContactNumber'),
      attorneyContact: t('wizard.fields.attorneyContact'),
      respondentNames: t('wizard.fields.respondentNames'),
      respondentType: t('wizard.fields.respondentType'),
      numberOfRespondents: t('wizard.fields.numberOfRespondents'),
      respondentAddress: t('wizard.fields.respondentAddress'),
      petitionerName: t('wizard.fields.petitionerName'),
      childUnder16: t('wizard.childUnder16'),
      selectFileCategory: t('wizard.placeholders.selectFileCategory'),
      selectSubFileCategory: t('wizard.placeholders.selectSubFileCategory'),
      selectApplicableAreas: t('wizard.placeholders.selectApplicableAreas'),
      selectProvince: t('wizard.placeholders.selectProvince'),
      selectDistrict: t('wizard.placeholders.selectDistrict'),
      selectCountry: t('wizard.placeholders.selectCountry'),
      loadingCountries: t('wizard.placeholders.loadingCountries'),
      briefDescriptionOfCase: t('wizard.placeholders.briefDescriptionOfCase'),
      briefDescriptionAdviceSought: t('wizard.placeholders.briefDescriptionAdviceSought'),
      sourceOfCase: t('wizard.placeholders.sourceOfCase'),
      selectCorporateInstitute: t('wizard.placeholders.selectCorporateInstitute'),
      enterOtherInstituteName: t('wizard.placeholders.enterOtherInstituteName'),
      enterExternalReferenceNumber: t('wizard.placeholders.enterExternalReferenceNumber'),
      commaSeparatedReferenceNumbers: t('wizard.placeholders.commaSeparatedReferenceNumbers'),
      commaSeparatedReferences: t('wizard.placeholders.commaSeparatedReferences'),
      courtReferenceNumber: t('wizard.placeholders.courtReferenceNumber'),
      includeConnectedCaseDetails: t('wizard.placeholders.includeConnectedCaseDetails'),
      enterConnectedCaseNumber: t('wizard.placeholders.enterConnectedCaseNumber'),
      enterConnectedAgdFileRef: t('wizard.placeholders.enterConnectedAgdFileRef'),
      uploadDocument: t('wizard.placeholders.uploadDocument'),
      uploadDocuments: t('wizard.sections.uploadDocuments'),
      incomingOutgoing: t('wizard.placeholders.incomingOutgoing'),
      policeMagistrateRef: t('wizard.placeholders.policeMagistrateRef'),
      connectedAgdHint: t('wizard.placeholders.connectedAgdHint'),
      enterAccusedName: t('wizard.placeholders.enterAccusedName'),
      enterVictimName: t('wizard.placeholders.enterVictimName'),
      selectNationality: t('wizard.placeholders.selectNationality'),
      loadingNationalities: t('wizard.placeholders.loadingNationalities'),
      selectCountryOfOrigin: t('wizard.placeholders.selectCountryOfOrigin'),
      selectGender: t('wizard.placeholders.selectGender'),
      selectCustodyStatus: t('wizard.placeholders.selectCustodyStatus'),
      loadingCustodyStatuses: t('wizard.placeholders.loadingCustodyStatuses'),
      custodialAuthorityHint: t('wizard.placeholders.custodialAuthorityHint'),
      selectUrgencyLevel: t('wizard.placeholders.selectUrgencyLevel'),
      urgencyJustificationReason: t('wizard.placeholders.urgencyJustificationReason'),
      docSkipReason: t('wizard.placeholders.docSkipReason'),
      specifyDeliveryMethod: t('wizard.placeholders.specifyDeliveryMethod'),
      enterPlaintiffApplicantName: t('wizard.placeholders.enterPlaintiffApplicantName'),
      enterDefendantRespondentName: t('wizard.placeholders.enterDefendantRespondentName'),
      enterLegalOfficerNameInCharge: t('wizard.placeholders.enterLegalOfficerNameInCharge'),
      otherRelatedInstitutionsHint: t('wizard.placeholders.otherRelatedInstitutionsHint'),
      otherRelatedInstitutionsPublicPrivateHint: t('wizard.placeholders.otherRelatedInstitutionsPublicPrivateHint'),
      enterInstitutionName: t('wizard.placeholders.enterInstitutionName'),
      addCorporation: t('wizard.placeholders.addCorporation'),
      remove: t('wizard.placeholders.remove'),
      contactNumberExample: t('wizard.placeholders.contactNumberExample'),
      specifyCourtType: t('wizard.placeholders.specifyCourtType'),
      enterCourtName: t('wizard.placeholders.enterCourtName'),
      nameOfTheCourt: t('wizard.placeholders.nameOfTheCourt'),
      detailedLegalIssueDescription: t('wizard.placeholders.detailedLegalIssueDescription'),
      selectNextStep: t('wizard.placeholders.selectNextStep'),
      enterNextStepOther: t('wizard.placeholders.enterNextStepOther'),
      enterRespondentNames: t('wizard.placeholders.enterRespondentNames'),
      enterRespondentAddress: t('wizard.placeholders.enterRespondentAddress'),
      enterNumberOfRespondents: t('wizard.placeholders.enterNumberOfRespondents'),
      enterPetitionerName: t('wizard.placeholders.enterPetitionerName'),
      enterAttorneyName: t('wizard.placeholders.enterAttorneyName'),
      selectRespondentType: t('wizard.placeholders.selectRespondentType'),
      enterRequestorName: t('wizard.placeholders.enterRequestorName'),
      enterNicOrPassport: t('wizard.placeholders.enterNicOrPassport'),
      enterAddress: t('wizard.placeholders.enterAddress'),
      enterEmailAddress: t('wizard.placeholders.enterEmailAddress'),
      enterPublicAuthorityName: t('wizard.placeholders.enterPublicAuthorityName'),
      describeRequestedInformation: t('wizard.placeholders.describeRequestedInformation'),
      enterTimePeriodIfApplicable: t('wizard.placeholders.enterTimePeriodIfApplicable'),
      selectPreferredLanguage: t('wizard.placeholders.selectPreferredLanguage'),
      provideJustification: t('wizard.placeholders.provideJustification'),
      additionalNotesOptional: t('wizard.placeholders.additionalNotesOptional'),
      enterPlaceOfOffence: t('wizard.placeholders.enterPlaceOfOffence'),
      enterAccusedSuspectName: t('wizard.placeholders.enterAccusedSuspectName'),
      selectStatus: t('wizard.placeholders.selectStatus'),
      specifyStatus: t('wizard.placeholders.specifyStatus'),
      enterUrgencyJustification: t('wizard.placeholders.enterUrgencyJustification'),
      caseSourceOther: t('wizard.placeholders.caseSourceOther'),
      selectMagistrateCourt: t('wizard.placeholders.selectMagistrateCourt'),
      selectHighCourt: t('wizard.placeholders.selectHighCourt'),
      selectPoliceStation: t('wizard.placeholders.selectPoliceStation'),
      loading: t('common.loading'),
      otherDeliveryMethod: t('wizard.fields.otherDeliveryMethod'),
      documentType: t('wizard.documents.documentType'),
      status: t('wizard.documents.status'),
      fileOrReason: t('wizard.documents.fileOrReason'),
      uploaded: t('wizard.documents.uploaded'),
      notUploaded: t('wizard.documents.notUploaded'),
      noDocumentsDeclared: t('wizard.documents.noDocumentsDeclared'),
      uploading: t('wizard.documents.uploading'),
      loadingChecklist: t('wizard.documents.loadingChecklist'),
      selectCategoryForDocs: t('wizard.documents.selectCategoryForDocs'),
      noDocsConfigured: t('wizard.documents.noDocsConfigured'),
      edit: t('wizard.edit'),
      yes: t('common.yes'),
      no: t('common.no'),
    }),
    [t],
  );
}
